import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { formatDate, getComicById, getRelatedComics } from "@/lib/comicUtils";

export default function Detail() {
  const params = useParams();
  const comicId = params.comicId || "";
  const comic = getComicById(comicId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  useDocumentMeta({
    title: comic
      ? `${comic.title} - 漫画详情 - 歪歪漫画在线观看`
      : "漫画详情 - 歪歪漫画在线观看",
    description: comic
      ? `${comic.title}：${comic.description.slice(0, 70)}`
      : "歪歪漫画在线观看，漫画详情页。",
    keywords: comic
      ? `${comic.title},${comic.genres.join(",")},歪歪漫画在线观看`
      : "歪歪漫画在线观看",
  });

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-100">
        <Navbar />
        <main className="mx-auto flex w-full max-w-6xl flex-col items-center justify-center px-4 py-32">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl text-foreground-900">该漫画不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">您访问的作品可能已被移除，去看看其他精彩作品吧。</p>
          <Link
            to="/category/全部"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-compass-3-line"></i>
            浏览全部漫画
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedComics(comic, 6);
  const firstChapter = comic.chapters[0];
  const latestChapter = comic.chapters[comic.chapters.length - 1];
  const favorited = isFavorite(comic.id);

  const handleFavorite = () => {
    const nowFavorited = toggleFavorite(comic.id);
    setToast(nowFavorited ? "收藏成功（仅前端演示）" : "已取消收藏");
  };

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-6xl px-4 md:px-8 py-6">
        <nav className="mb-4 flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <Link to={`/category/${encodeURIComponent(comic.genres[0])}`} className="hover:text-primary-600">
            {comic.genres[0]}漫画
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-700">{comic.title}</span>
        </nav>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <div className="flex flex-col gap-6 md:flex-row">
            <div className="mx-auto h-[320px] w-[228px] shrink-0 overflow-hidden rounded-lg bg-background-200 md:mx-0">
              <img
                src={comic.cover}
                alt={`${comic.title} 漫画封面`}
                title={`${comic.title} 漫画详情`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex min-w-0 flex-1 flex-col">
              <h1 className="font-heading text-2xl text-foreground-950 md:text-3xl">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5"><i className="ri-user-line text-foreground-400"></i> {comic.author}</span>
                <span className="flex items-center gap-1.5"><i className="ri-earth-line text-foreground-400"></i> {comic.region}</span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line text-foreground-400"></i>
                  <span className={comic.status === "连载中" ? "text-primary-600" : "text-secondary-600"}>{comic.status}</span>
                </span>
                <span className="flex items-center gap-1.5"><i className="ri-calendar-line text-foreground-400"></i> 更新于 {formatDate(comic.updatedAt)}</span>
                <span className="flex items-center gap-1.5"><i className="ri-fire-line text-foreground-400"></i> {(comic.views / 10000).toFixed(1)}万 人气</span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {comic.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${encodeURIComponent(g)}`}
                    className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800 hover:bg-accent-200"
                  >
                    {g}
                  </Link>
                ))}
              </div>

              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">{comic.description}</p>

              <div className="mt-auto flex flex-wrap gap-3 pt-6">
                {firstChapter && (
                  <Link
                    to={`/read/${comic.id}/${firstChapter.id}`}
                    className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-book-read-line"></i>
                    开始阅读
                  </Link>
                )}
                {latestChapter && (
                  <Link
                    to={`/read/${comic.id}/${latestChapter.id}`}
                    className="inline-flex items-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-timer-flash-line"></i>
                    查看最新章
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 rounded-md border px-5 py-2.5 text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    favorited
                      ? "border-accent-400 bg-accent-100 text-accent-800"
                      : "border-background-300 text-foreground-700 hover:border-primary-400"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
                  {favorited ? "已收藏" : "收藏"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-heading text-xl text-foreground-950">章节目录</h2>
            <span className="text-sm text-foreground-500">共 {comic.chapters.length} 话</span>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {comic.chapters.map((chapter) => (
              <Link
                key={chapter.id}
                to={`/read/${comic.id}/${chapter.id}`}
                className="flex items-center justify-between gap-2 rounded-md border border-background-200 px-3 py-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50/50"
              >
                <span className="line-clamp-1 text-sm text-foreground-800">{chapter.name}</span>
                <span className="shrink-0 text-xs text-foreground-400">{formatDate(chapter.updatedAt)}</span>
              </Link>
            ))}
          </div>
        </section>

        {related.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-5 font-heading text-xl text-foreground-950">相关推荐</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {related.map((item) => (
                <ComicCard key={item.id} comic={item} />
              ))}
            </div>
          </section>
        )}

        <div className="mt-8">
          <Link
            to={`/category/${encodeURIComponent(comic.genres[0])}`}
            className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
          >
            <i className="ri-arrow-left-line"></i>
            返回{comic.genres[0]}分类
          </Link>
        </div>
      </main>

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-900 px-5 py-2.5 text-sm text-background-50 animate-fade-up">
          {toast}
        </div>
      )}
      <Footer />
    </div>
  );
}